import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../services/axiosInstance'
import PageHeader from '../../components/common/PageHeader'
import { Search, MapPin, ExternalLink, GraduationCap } from 'lucide-react'
import './styles.scss'

export default function UniversityDiscovery() {
  const navigate = useNavigate()
  const [universities, setUniversities] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  
  // Modal State
  const [selectedUniv, setSelectedUniv] = useState(null)
  const [programs, setPrograms] = useState([])
  const [intakes, setIntakes] = useState([])
  const [selectedProgram, setSelectedProgram] = useState('')
  const [selectedIntake, setSelectedIntake] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    fetchUniversities()
  }, [])

  const fetchUniversities = async () => {
    try {
      const res = await axiosInstance.get('/api/universities')
      setUniversities(res.data)
    } catch (e) {
      console.error('Failed to load universities', e)
    } finally {
      setLoading(false)
    }
  }

  const handleApplyClick = async (univ) => {
    setSelectedUniv(univ)
    try {
      const [progRes, intakeRes] = await Promise.all([
        axiosInstance.get(`/api/applications/programs?universityId=${univ.id}`),
        axiosInstance.get(`/api/applications/intakes?universityId=${univ.id}`)
      ])
      setPrograms(progRes.data?.data || [])
      setIntakes(intakeRes.data?.data || [])
    } catch (e) {
      console.error('Failed to load programs/intakes', e)
    }
  }

  const submitApplication = async () => {
    if (!selectedProgram || !selectedIntake) return
    setIsSubmitting(true)
    try {
      await axiosInstance.post('/api/applications/me', {
        programId: selectedProgram,
        intakeId: selectedIntake
      })
      setSelectedUniv(null)
      navigate('/applications')
    } catch (e) {
      console.error('Failed to create app', e)
      alert(e.response?.data?.message || 'Failed to create application')
    } finally {
      setIsSubmitting(false)
    }
  }

  const filtered = universities.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.country?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="page-container university-discovery">
      <PageHeader 
        title="Discover Universities" 
        subtitle="Find and apply to our partner institutions across the globe."
      />

      <div className="discovery-toolbar">
        <div className="search-box">
          <Search size={18} className="icon" />
          <input 
            type="text" 
            placeholder="Search universities or countries..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(37,99,235,0.2)', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : (
        <div className="university-grid">
          {filtered.map(univ => (
            <div className="university-card" key={univ.id}>
              <div className="university-card__banner" style={{ background: univ.themeColor || '#2563eb' }}>
                <img src={univ.logoUrl || '/campusready_logo.svg'} alt={univ.name} className="logo" />
              </div>
              <div className="university-card__body">
                <h3>{univ.name}</h3>
                <div className="info-row">
                  <MapPin size={14} /> <span>{univ.city}, {univ.country}</span>
                </div>
                <div className="info-row">
                  <GraduationCap size={14} /> <span>{univ.type || 'Public University'}</span>
                </div>
                <p className="desc">{univ.description || 'A leading institution offering top-tier academic programs and research opportunities.'}</p>
                <div className="actions">
                  <button className="btn btn-primary btn-full" onClick={() => handleApplyClick(univ)}>
                    Apply Now
                  </button>
                  {univ.websiteUrl && (
                    <a href={univ.websiteUrl} target="_blank" rel="noreferrer" className="btn btn-outline icon-btn">
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
             <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#64748b' }}>
               No universities found matching your search.
             </div>
          )}
        </div>
      )}

      {/* Apply Modal */}
      {selectedUniv && (
        <div className="modal-overlay" onClick={() => setSelectedUniv(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Apply to {selectedUniv.name}</h3>
              <button className="close-btn" onClick={() => setSelectedUniv(null)}>&times;</button>
            </div>
            <div className="modal-body form-layout">
              <div className="form-group">
                <label>Select Program</label>
                <select value={selectedProgram} onChange={e => setSelectedProgram(e.target.value)}>
                  <option value="">-- Choose a Program --</option>
                  {programs.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Select Intake Term</label>
                <select value={selectedIntake} onChange={e => setSelectedIntake(e.target.value)}>
                  <option value="">-- Choose an Intake --</option>
                  {intakes.map(i => (
                    <option key={i.id} value={i.id}>{i.term} {i.year}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelectedUniv(null)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                disabled={!selectedProgram || !selectedIntake || isSubmitting}
                onClick={submitApplication}
              >
                {isSubmitting ? 'Creating...' : 'Start Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
